import { useState, type FormEvent } from "react";
import {
  Check,
  KeyRound,
  MoreHorizontal,
  Plus,
  Shield,
  Trash2,
  User,
  UserCheck,
  UserMinus,
  UserPlus,
  X,
} from "lucide-react";
import { USER_ROLE_OPTIONS, type UserRole, type UserSummary } from "../contract.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { formatDateTime } from "../lib/format.js";
import { useCreateUserMutation, useListUsersQuery, useUpdateUserMutation } from "../store/api.js";
import { useAppSelector } from "../store/index.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Administrator",
  releaser: "Perilis",
  analyst: "Analis",
  viewer: "Peninjau",
};

export function UsersPage() {
  const signedInUserId = useAppSelector((state) => state.auth.user?.id);
  const users = useListUsersQuery();
  const [updateUser, updateState] = useUpdateUserMutation();
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<UserSummary | null>(null);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#101828]">Pengguna</h1>
          <p className="text-[13.5px] text-[#667085]">
            Akun yang memiliki hak akses masuk ke Konsol OTA, beserta perannya masing-masing.
          </p>
        </div>

        <Button
          className="h-9 gap-1.5 bg-[#0F1B3D] text-white hover:bg-[#1D2939] text-xs font-medium shadow-xs"
          onClick={() => setCreateOpen(true)}
        >
          <UserPlus className="size-4" />
          Pengguna baru
        </Button>
      </div>

      {/* Users Table */}
      <div className="rounded-xl border border-[#EAECF0] bg-white shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-[#EAECF0] bg-[#F6F7F9]/70 text-[#667085]">
                <th className="py-3 px-4 font-medium">Nama Pengguna</th>
                <th className="py-3 px-4 font-medium">Peran</th>
                <th className="py-3 px-4 font-medium">Status Akun</th>
                <th className="py-3 px-4 font-medium">Login Terakhir</th>
                <th className="py-3 px-4 font-medium">Dibuat Pada</th>
                <th className="py-3 px-4 font-medium text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAECF0]">
              {users.data?.map((user) => {
                const isSelf = user.id === signedInUserId;
                const initials = user.username.slice(0, 2).toUpperCase();

                return (
                  <tr key={user.id} className="hover:bg-[#F6F7F9] transition-colors">
                    {/* Username & Avatar */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2.5">
                        <div className="size-8 rounded-full bg-[#0F1B3D] text-white flex items-center justify-center font-bold text-xs shrink-0">
                          {initials}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-[#101828] font-mono">
                              {user.username}
                            </span>
                            {isSelf && (
                              <span className="rounded bg-[#EFF4FF] border border-[#BFDBFE] px-1.5 py-0.2 text-[10px] font-semibold text-[#2563EB]">
                                Anda
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] font-mono text-[#667085] break-all block">ID: {user.id}</span>
                        </div>
                      </div>
                    </td>

                    {/* Role Select */}
                    <td className="py-3 px-4">
                      <select
                        value={user.role}
                        disabled={isSelf || updateState.isLoading}
                        onChange={(e) =>
                          void updateUser({ id: user.id, role: e.target.value as UserRole })
                        }
                        className="h-8 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#101828] focus:outline-none focus:border-[#2563EB] disabled:bg-[#F6F7F9] disabled:text-[#667085]"
                      >
                        {USER_ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Status */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            user.disabled
                              ? "bg-[#FEF3F2] border border-[#FECDCA] text-[#B42318]"
                              : "bg-[#ECFDF3] border border-[#ABEFC6] text-[#027A48]"
                          }`}
                        >
                          <span
                            className={`size-1.5 rounded-full ${
                              user.disabled ? "bg-[#F04438]" : "bg-[#17B26A]"
                            }`}
                          />
                          {user.disabled ? "Dinonaktifkan" : "Aktif"}
                        </span>
                        {user.mustChangePassword && (
                          <span className="rounded-full bg-[#FFFAEB] border border-[#FEDF89] px-2 py-0.5 text-[11px] font-medium text-[#B54708]">
                            Wajib ganti sandi
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Login Terakhir */}
                    <td className="py-3 px-4 text-[#667085] font-mono whitespace-nowrap">
                      {formatDateTime(user.lastLoginAt)}
                    </td>

                    {/* Dibuat */}
                    <td className="py-3 px-4 text-[#667085] font-mono whitespace-nowrap">
                      {formatDateTime(user.createdAt)}
                    </td>

                    {/* Aksi */}
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1 border-[#D0D5DD] text-[#344054]"
                          onClick={() => setResetTarget(user)}
                        >
                          <KeyRound className="size-3 text-[#667085]" />
                          Reset sandi
                        </Button>

                        {!isSelf && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-7 text-xs ${user.disabled ? "text-[#17B26A]" : "text-[#F04438]"}`}
                            onClick={() =>
                              void updateUser({ id: user.id, disabled: !user.disabled })
                            }
                          >
                            {user.disabled ? "Aktifkan" : "Nonaktifkan"}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {createOpen && <CreateUserModal onClose={() => setCreateOpen(false)} />}

      {/* Reset Password Modal */}
      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
    </div>
  );
}

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const [createUser, { isLoading, error }] = useCreateUserMutation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("viewer");
  const [mustChangePassword, setMustChangePassword] = useState(true);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const result = await createUser({
      username: username.trim(),
      password,
      role,
      mustChangePassword,
    });
    if (!("error" in result)) onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl border border-[#EAECF0]">
        <div className="flex items-center justify-between pb-3 border-b border-[#EAECF0]">
          <h3 className="text-base font-bold text-[#101828]">Buat Pengguna Baru</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-[#98A2B3] hover:text-[#101828]">
            <X className="size-4" />
          </button>
        </div>

        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-[#344054]">Nama Pengguna</Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="mis. operator.prod"
              className="h-9 text-xs border-[#D0D5DD]"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-[#344054]">Kata Sandi Awal</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimal 8 karakter"
              className="h-9 text-xs border-[#D0D5DD]"
              required
              minLength={8}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-[#344054]">Peran Akses</Label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="w-full h-9 rounded-lg border border-[#D0D5DD] bg-white px-3 text-xs text-[#101828] focus:outline-none"
            >
              {USER_ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 pt-1 text-xs text-[#344054]">
            <input
              type="checkbox"
              id="mustChange"
              checked={mustChangePassword}
              onChange={(e) => setMustChangePassword(e.target.checked)}
              className="rounded border-[#D0D5DD] text-[#2563EB]"
            />
            <label htmlFor="mustChange" className="cursor-pointer">
              Wajib ganti kata sandi pada login pertama
            </label>
          </div>

          {error && (
            <p className="text-xs text-[#F04438]">{apiErrorMessage(error)}</p>
          )}

          <div className="flex justify-end gap-2.5 pt-3 border-t border-[#EAECF0]">
            <Button variant="outline" size="sm" type="button" onClick={onClose} className="text-xs">
              Batal
            </Button>
            <Button
              size="sm"
              type="submit"
              disabled={isLoading}
              className="bg-[#0F1B3D] text-white text-xs hover:bg-[#1D2939]"
            >
              {isLoading ? "Menyimpan…" : "Buat Pengguna"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose }: { user: UserSummary; onClose: () => void }) {
  const [updateUser, { isLoading, error }] = useUpdateUserMutation();
  const [newPassword, setNewPassword] = useState("");
  const [mustChangePassword, setMustChangePassword] = useState(true);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const result = await updateUser({
      id: user.id,
      newPassword,
      mustChangePassword,
    });
    if (!("error" in result)) onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl border border-[#EAECF0]">
        <div className="flex items-center justify-between pb-3 border-b border-[#EAECF0]">
          <div>
            <h3 className="text-base font-bold text-[#101828]">Reset Kata Sandi</h3>
            <p className="text-xs text-[#667085]">Pengguna: {user.username}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-[#98A2B3] hover:text-[#101828]">
            <X className="size-4" />
          </button>
        </div>

        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-[#344054]">Kata Sandi Baru</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimal 8 karakter"
              className="h-9 text-xs border-[#D0D5DD]"
              required
              minLength={8}
            />
          </div>

          <div className="flex items-center gap-2 pt-1 text-xs text-[#344054]">
            <input
              type="checkbox"
              id="resetMustChange"
              checked={mustChangePassword}
              onChange={(e) => setMustChangePassword(e.target.checked)}
              className="rounded border-[#D0D5DD] text-[#2563EB]"
            />
            <label htmlFor="resetMustChange" className="cursor-pointer">
              Wajib ganti kata sandi pada login berikutnya
            </label>
          </div>

          {error && <p className="text-xs text-[#F04438]">{apiErrorMessage(error)}</p>}

          <div className="flex justify-end gap-2.5 pt-3 border-t border-[#EAECF0]">
            <Button variant="outline" size="sm" type="button" onClick={onClose} className="text-xs">
              Batal
            </Button>
            <Button
              size="sm"
              type="submit"
              disabled={isLoading}
              className="bg-[#0F1B3D] text-white text-xs hover:bg-[#1D2939]"
            >
              {isLoading ? "Menyimpan…" : "Simpan Sandi Baru"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
